if (!customElements.get('testimonials-gsap')) {
  class TestimonialsGSAP extends HTMLElement {
    constructor() {
      super();
      this.total = 0;
      this.xTo = null;
      this.observer = null;
      this.hasMoved = false;
      this.isDragging = false;
      this.isScrolling = false;
      this.boundTick = null;
      this.tickSpeed = 0;
    }

    connectedCallback() {
      // Wait for GSAP to load
      if (typeof gsap === 'undefined' || typeof Observer === 'undefined') {
        setTimeout(() => this.connectedCallback(), 100);
        return;
      }

      this.init();
    }

    init() {
      gsap.registerPlugin(Observer);

      const container = this.querySelector('.testimonials-gsap__container');
      const content = this.querySelector('.testimonials-gsap__content');

      if (!container || !content) {
        console.warn('Testimonials GSAP: Required elements not found');
        return;
      }

      // Clone content for infinite scroll effect
      const clone = content.cloneNode(true);
      container.appendChild(clone);

      // Calculate wrap point (width of one set of content)
      const contentWidth = content.offsetWidth;
      this.wrapPoint = -contentWidth;

      // Wrapping function for infinite scroll
      const wrap = gsap.utils.wrap(this.wrapPoint, 0);

      // Setup quickTo for smooth dragging
      this.xTo = gsap.quickTo(container, "x", {
        duration: 0.8,
        ease: "power3.out",
        modifiers: {
          x: gsap.utils.unitize(wrap)
        }
      });

      // Store wrap function for later use
      this.wrap = wrap;

      // Auto-scroll setup
      const speed = parseFloat(this.getAttribute('speed')) || 50;
      this.startAutoScroll(speed);

      // GSAP Observer for drag/swipe
      this.observer = Observer.create({
        target: container,
        type: "pointer,touch",
        preventDefault: true,
        onPress: () => {
          this.hasMoved = false;
          this.isDragging = true;
          this.stopAutoScroll();
          container.style.cursor = 'grabbing';
        },
        onDrag: (self) => {
          this.total += self.deltaX;
          this.xTo(this.total);

          if (Math.abs(self.deltaX) > 5) {
            this.hasMoved = true;
          }
        },
        onRelease: () => {
          this.isDragging = false;
          container.style.cursor = 'grab';

          // Resume auto-scroll after a delay
          setTimeout(() => {
            const speed = parseFloat(this.getAttribute('speed')) || 50;
            this.startAutoScroll(speed);
          }, 1000);
        },
        onStop: () => {
          this.isDragging = false;
          container.style.cursor = 'grab';
        },
        onClick: (self) => {
          if (this.hasMoved) {
            self.event.preventDefault();
            self.event.stopPropagation();
          }
          this.hasMoved = false;
        }
      });
    }

    startAutoScroll(speed) {
      // Stop any existing auto-scroll
      this.stopAutoScroll();

      if (speed <= 0) return;

      this.tickSpeed = speed / 60; // Convert to per-frame speed
      this.isScrolling = true;

      // Bind tick function once and store reference
      this.boundTick = this.tick.bind(this);
      gsap.ticker.add(this.boundTick);
    }

    stopAutoScroll() {
      if (this.boundTick) {
        gsap.ticker.remove(this.boundTick);
        this.boundTick = null;
      }
      this.isScrolling = false;
    }

    tick() {
      // Don't scroll if dragging
      if (!this.xTo || this.isDragging || !this.isScrolling) return;

      this.total -= this.tickSpeed;
      this.xTo(this.total);
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.kill();
      }
      this.stopAutoScroll();
    }

    static get observedAttributes() {
      return ['speed'];
    }

    attributeChangedCallback(name, oldVal, newVal) {
      if (name === 'speed' && oldVal !== newVal && this.xTo) {
        this.stopAutoScroll();
        const speed = parseFloat(newVal) || 50;
        this.startAutoScroll(speed);
      }
    }
  }

  customElements.define('testimonials-gsap', TestimonialsGSAP);
}
